import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ArrowRight, Users, Plug, UserPlus, X } from 'lucide-react';

const CHECKLIST_ITEMS = [
  {
    id: 'create-join-team',
    label: 'Create or Join a Team',
    description: 'Set up your workspace by creating a new team or joining via an invite link.',
    icon: Users,
  },
  {
    id: 'connect-workflow',
    label: 'Connect Your Workflow & Data Sources',
    description: 'Import from Notion, Asana, or Jira, or connect game data sources.',
    icon: Plug,
  },
  {
    id: 'invite-members',
    label: 'Invite Your Team Members',
    description: 'Add collaborators and assign roles so the right people can contribute.',
    icon: UserPlus,
  },
];

/** Delay in ms before the nudge slides in. */
const APPEAR_DELAY = 2000;

const EXCLUDED_PATHS = ['/auth', '/auth/reset-password', '/join', '/onboarding', '/getting-started', '/unsubscribe'];

export const OnboardingSheet: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Load persisted progress
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from('onboarding_checklist_progress' as any)
        .select('completed_items')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) {
        const items: string[] = (data as any).completed_items ?? [];
        const map: Record<string, boolean> = {};
        items.forEach((id: string) => { map[id] = true; });
        setChecked(map);
      }
      setLoaded(true);
    };
    load();
  }, [user]);

  // Auto-show with delay when loaded and incomplete
  useEffect(() => {
    if (!loaded || !user || dismissed) return;
    if (EXCLUDED_PATHS.some((p) => location.pathname.startsWith(p))) return;

    const completedCount = CHECKLIST_ITEMS.filter((item) => checked[item.id]).length;
    if (completedCount >= CHECKLIST_ITEMS.length) return;

    const timer = setTimeout(() => setVisible(true), APPEAR_DELAY);
    return () => clearTimeout(timer);
  }, [loaded, user, dismissed, location.pathname, checked]);

  // Persist checklist progress
  const persistChecklist = useCallback(
    async (next: Record<string, boolean>) => {
      if (!user) return;
      const completed = Object.entries(next).filter(([, v]) => v).map(([k]) => k);
      await supabase
        .from('onboarding_checklist_progress' as any)
        .upsert({ user_id: user.id, completed_items: completed } as any, { onConflict: 'user_id' });
    },
    [user],
  );

  const toggleItem = (id: string) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persistChecklist(next);
      return next;
    });
  };

  const dismiss = () => {
    setVisible(false);
    setDismissed(true);
  };

  if (!user || !loaded || !visible) return null;

  const completedCount = CHECKLIST_ITEMS.filter((item) => checked[item.id]).length;

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 animate-in slide-in-from-right-full duration-300">
      <div className="rounded-xl border bg-popover text-popover-foreground shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 p-4 pb-2">
          <div>
            <p className="font-semibold text-sm leading-tight">Get Started with Arcanix</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pilot phase. We'd love your feedback.
            </p>
          </div>
          <button onClick={dismiss} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${(completedCount / CHECKLIST_ITEMS.length) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{completedCount}/{CHECKLIST_ITEMS.length}</span>
          </div>
        </div>

        {/* Checklist */}
        <div className="px-4 pb-2 space-y-2">
          {CHECKLIST_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <label
                key={item.id}
                htmlFor={`onb-${item.id}`}
                className={`flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors text-left ${checked[item.id] ? 'border-primary/30 bg-primary/5' : 'hover:bg-muted/50'}`}
              >
                <Checkbox
                  id={`onb-${item.id}`}
                  checked={!!checked[item.id]}
                  onCheckedChange={() => toggleItem(item.id)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className={`font-medium text-xs ${checked[item.id] ? 'line-through text-muted-foreground' : ''}`}>
                      {item.label}
                    </span>
                  </span>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{item.description}</p>
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 pt-2">
          <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => { dismiss(); navigate('/getting-started'); }}>
            View full guide <ArrowRight className="ml-1.5 h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};
